import { useState, useEffect } from 'react'
import { differenceInSeconds } from 'date-fns'
import { padZero } from '../util/padZero'
import { TimeDigits } from '../constants/types'
import { useUnit } from 'effector-react'
import { $premiumLevel, PremiumLevel } from '@/entities/user'

export type TimerState = {
  timeLeft: TimeDigits
  progressBarWidth: number
  canUpdate: boolean
  skipPrice: number
}

function getMaxCooldownSeconds(level: PremiumLevel | null): number {
  switch (level) {
    case PremiumLevel.PLATINUM:
      return 43200
    case PremiumLevel.PLUS:
      return 86400
    default:
      return 86400
  }
}

function getTimerState(
  nextUpdateDate: string | null,
  lastClaimDate: string | null,
  subscriptionLevel: PremiumLevel | null
): TimerState {
  let timeLeft: TimeDigits = { days: '00', hours: '00', minutes: '00', seconds: '00' }
  let progressBarWidth = 0
  let canUpdate = false
  let skipPrice = 0

  if (!nextUpdateDate) {
    return { timeLeft, progressBarWidth, canUpdate, skipPrice }
  }

  const now = new Date()
  const targetDate = new Date(nextUpdateDate)

  if (isNaN(targetDate.getTime())) {
    return {
      timeLeft: { days: '00', hours: '23', minutes: '59', seconds: '59' },
      progressBarWidth,
      canUpdate,
      skipPrice,
    }
  }

  const totalRemainingSeconds = differenceInSeconds(targetDate, now)

  if (totalRemainingSeconds <= 0) {
    canUpdate = true
    progressBarWidth = 100
    return { timeLeft, progressBarWidth, canUpdate, skipPrice: 0 }
  }

  const days = Math.floor(totalRemainingSeconds / 86400)
  const hours = Math.floor((totalRemainingSeconds % 86400) / 3600)
  const minutes = Math.floor((totalRemainingSeconds % 3600) / 60)
  const seconds = totalRemainingSeconds % 60

  timeLeft = {
    days: String(days),
    hours: padZero(hours),
    minutes: padZero(minutes),
    seconds: padZero(seconds),
  }

  if (lastClaimDate) {
    const lastDate = new Date(lastClaimDate)
    const totalDiff = differenceInSeconds(lastDate, targetDate)
    const elapsed = differenceInSeconds(lastDate, now)
    const percentElapsed = (elapsed / totalDiff) * 100
    progressBarWidth = Math.max(0, Math.min(percentElapsed, 100))
  }

  const maxCooldown = getMaxCooldownSeconds(subscriptionLevel)
  const MAX_SKIP_PRICE = 100

  if (totalRemainingSeconds > 0) {
    skipPrice = Math.max(1, Math.min(99, Math.round((totalRemainingSeconds / maxCooldown) * MAX_SKIP_PRICE)))
  }

  return { timeLeft, progressBarWidth, canUpdate, skipPrice }
}

export function useTimer(nextUpdateDate: string | null, lastClaimDate?: string | null, disabled: boolean = false) {
  const subscriptionLevel = useUnit($premiumLevel)

  const [timerState, setTimerState] = useState<TimerState>(() =>
    getTimerState(nextUpdateDate, lastClaimDate ?? null, subscriptionLevel)
  )

  useEffect(() => {
    if (disabled) return

    const intervalId = setInterval(() => {
      setTimerState(getTimerState(nextUpdateDate, lastClaimDate ?? null, subscriptionLevel))
    }, 1000)

    return () => clearInterval(intervalId)
  }, [nextUpdateDate, lastClaimDate, subscriptionLevel, disabled])

  return {
    timeLeft: timerState.timeLeft,
    progressBarWidth: timerState.progressBarWidth,
    canUpdate: timerState.canUpdate,
    skipPrice: timerState.skipPrice,
  }
}
